import { FC, ReactNode } from 'react';

import { getStaticPrefix } from '@eneco/common/env';
import RichText from '@eneco/components/RichText/RichText';
import { TrackedDialog } from '@eneco/components/TrackedDialog/TrackedDialog';
import useDC from '@eneco/dc/useDC';
import { I18nProvider, useTranslation } from '@eneco/i18n';
import { Stack } from '@eneco/sparky';
import {
  DC_Domain_Models_Products_ProductType,
  Usages_Reading_CounterModel,
  Usages_Reading_MeterModel,
} from '@monorepo-types/dc';

import { MeterArticle } from './components/MeterArticle/MeterArticle';
import { MeterDetails } from './components/MeterDetails/MeterDetails';

type CounterInstructionDialogProps = {
  productType: DC_Domain_Models_Products_ProductType;
  isSmart: boolean;
  counter: Usages_Reading_CounterModel;
  meterNumber: Usages_Reading_MeterModel['meterNumber'];
  isDoubleMeter: boolean;
  reading: number;
  trigger: ReactNode;
};

type DialogVariants =
  | 'conventionalDoubleMeterElectricityLow'
  | 'conventionalDoubleMeterElectricityHigh'
  | 'conventionalDoubleMeterElectricityRedeliveryLow'
  | 'conventionalDoubleMeterElectricityRedeliveryHigh'
  | 'conventionalSingleMeterElectricity'
  | 'smartMeterElectricityLow'
  | 'smartMeterElectricityHigh'
  | 'smartMeterElectricityRedeliveryLow'
  | 'smartMeterElectricityRedeliveryHigh'
  | 'conventionalMeterGas'
  | 'smartMeterGas'
  | 'warmthGJMeter'
  | 'warmthM3Meter'
  | 'coolingGJMeter'
  | 'coolingM3Meter'
  | 'waterMeter';

const getDialogVariant = ({
  productType,
  isDoubleMeter,
  isSmart,
  counter,
}: CounterInstructionDialogProps): DialogVariants | false => {
  if (!isSmart && isDoubleMeter && counter.counterType === 'ElectricityLow')
    return 'conventionalDoubleMeterElectricityLow';
  if (!isSmart && isDoubleMeter && counter.counterType === 'ElectricityHigh')
    return 'conventionalDoubleMeterElectricityHigh';
  if (!isSmart && isDoubleMeter && counter.counterType === 'ElectricityRedeliveryLow')
    return 'conventionalDoubleMeterElectricityRedeliveryLow';
  if (!isSmart && isDoubleMeter && counter.counterType === 'ElectricityRedeliveryHigh')
    return 'conventionalDoubleMeterElectricityRedeliveryHigh';
  if (productType === 'electricity' && !isSmart && !isDoubleMeter) return 'conventionalSingleMeterElectricity';
  if (isSmart && counter.counterType === 'ElectricityLow') return 'smartMeterElectricityLow';
  if (isSmart && counter.counterType === 'ElectricityHigh') return 'smartMeterElectricityHigh';
  if (isSmart && counter.counterType === 'ElectricityRedeliveryLow') return 'smartMeterElectricityRedeliveryLow';
  if (isSmart && counter.counterType === 'ElectricityRedeliveryHigh') return 'smartMeterElectricityRedeliveryHigh';
  if (!isSmart && counter.counterType === 'Gas') return 'conventionalMeterGas';
  if (isSmart && counter.counterType === 'Gas') return 'smartMeterGas';
  if (counter.counterType === 'WarmthGJ') return 'warmthGJMeter';
  if (counter.counterType === 'WarmthM3') return 'warmthM3Meter';
  if (counter.counterType === 'CoolingGJ') return 'coolingGJMeter';
  if (counter.counterType === 'CoolingM3') return 'coolingM3Meter';
  if (counter.counterType === 'Water') return 'waterMeter';
  return false;
};

export const CounterInstructionDialog: FC<CounterInstructionDialogProps> = props => {
  const { label } = useDC();
  const variant = getDialogVariant(props);

  if (!variant) return null;

  return (
    <I18nProvider dictionary={locale => import(`./content/${props.productType}/${variant}.${label}.${locale}.json`)}>
      <CounterInstructionDialogComponent {...props} />
    </I18nProvider>
  );
};

type CounterInstructionDialogComponentProps = Pick<
  CounterInstructionDialogProps,
  'counter' | 'reading' | 'trigger' | 'meterNumber'
>;

export const CounterInstructionDialogComponent: FC<CounterInstructionDialogComponentProps> = ({
  counter,
  reading,
  trigger,
  meterNumber,
}) => {
  const { t } = useTranslation();
  const articles = [`firstArticle`, `secondArticle`, `thirdArticle`].filter(key => t(`${key}.description`));

  return (
    <TrackedDialog title={t(`title`)} trigger={trigger}>
      <Stack gap="8">
        <Stack gap="4">
          <RichText html={t(`description`)} />
          <MeterDetails
            meterNumber={meterNumber}
            meterType={t(`meterType`)}
            meterValue={`${reading} ${counter.denotation}`}
          />
        </Stack>
        <Stack gap="10">
          {articles.map(article => {
            const meterImageSrc = t(`${article}.meterImage.src`);
            const counterImageSrc = t(`${article}.counterImage.src`);

            return (
              <MeterArticle
                key={article}
                title={t(`${article}.title`)}
                description={t(`${article}.description`)}
                meterImage={{
                  src: `${getStaticPrefix()}${meterImageSrc}`,
                  alt: t(`${article}.meterImage.alt`),
                }}
                counterImage={{
                  src: `${getStaticPrefix()}${counterImageSrc}`,
                  alt: t(`${article}.counterImage.alt`),
                }}
              />
            );
          })}
        </Stack>
      </Stack>
    </TrackedDialog>
  );
};
